export type KnownPlaceholderMatch = {
  key: string;
  start: number;
  end: number;
};

const INLINE_ELEMENTS = new Set([
  'A',
  'ABBR',
  'ACRONYM',
  'B',
  'BDI',
  'BDO',
  'BIG',
  'CITE',
  'CODE',
  'DEL',
  'DFN',
  'EM',
  'FONT',
  'I',
  'INS',
  'KBD',
  'MARK',
  'Q',
  'S',
  'SAMP',
  'SMALL',
  'SPAN',
  'STRIKE',
  'STRONG',
  'SUB',
  'SUP',
  'TIME',
  'TT',
  'U',
  'VAR',
]);
const INLINE_DISPLAY_VALUES = new Set([
  'inline',
  'inline-block',
  'inline-flex',
  'inline-grid',
  'inline-table',
  'contents',
]);

export function isInlineTextFlowElement(element: Element): boolean {
  if (!INLINE_ELEMENTS.has(element.tagName)) return false;
  const display = (element as HTMLElement).style?.display.trim().toLowerCase() ?? '';
  return display === '' || INLINE_DISPLAY_VALUES.has(display);
}

export function findKnownPlaceholders(text: string, knownKeys: readonly string[]): KnownPlaceholderMatch[] {
  if (!text || knownKeys.length === 0) return [];

  const knownKeySet = new Set(knownKeys);
  const matches: KnownPlaceholderMatch[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const start = text.indexOf('{{', cursor);
    if (start < 0) break;

    const close = text.indexOf('}}', start + 2);
    if (close < 0) break;

    const key = text.slice(start + 2, close);
    const end = close + 2;
    if (key.length > 0 && !/\s/.test(key) && knownKeySet.has(key)) {
      matches.push({ key, start, end });
      cursor = end;
    } else {
      cursor = start + 2;
    }
  }

  return matches;
}

type CharacterLocation = { nodeIndex: number; offset: number };
const BREAK_LOCATION: CharacterLocation = { nodeIndex: -1, offset: -1 };

function collectTextNodes(root: Node): Text[] {
  const textNodes: Text[] = [];
  const walker = (root.ownerDocument ?? document).createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }
  return textNodes;
}

function nearestTextFlowContainer(node: Node, root: Node): Node {
  let current = node.parentNode;
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE && !isInlineTextFlowElement(current as Element)) return current;
    current = current.parentNode;
  }
  return root;
}

function nextNodeInDocumentOrder(node: Node): Node | null {
  if (node.firstChild) return node.firstChild;

  let current: Node | null = node;
  while (current) {
    if (current.nextSibling) return current.nextSibling;
    current = current.parentNode;
  }
  return null;
}

function hasVisibleBreakBetween(previous: Text, next: Text, root: Node): boolean {
  if (nearestTextFlowContainer(previous, root) !== nearestTextFlowContainer(next, root)) return true;

  let current = nextNodeInDocumentOrder(previous);
  while (current && current !== next) {
    if (current.nodeType === Node.ELEMENT_NODE && !isInlineTextFlowElement(current as Element)) return true;
    current = nextNodeInDocumentOrder(current);
  }
  return false;
}

function buildVisibleTextMap(textNodes: Text[], root: Node): { text: string; locations: CharacterLocation[] } {
  let text = '';
  const locations: CharacterLocation[] = [];

  textNodes.forEach((textNode, nodeIndex) => {
    if (nodeIndex > 0 && hasVisibleBreakBetween(textNodes[nodeIndex - 1], textNode, root)) {
      text += '\n';
      locations.push(BREAK_LOCATION);
    }
    for (let offset = 0; offset < textNode.data.length; offset += 1) {
      text += textNode.data[offset];
      locations.push({ nodeIndex, offset });
    }
  });

  return { text, locations };
}

/** Reads token boundaries across inline nodes without changing browser-owned DOM or undo history. */
export function findKnownPlaceholderTextRanges(
  root: HTMLElement,
  knownKeys: readonly string[],
): Array<{ start: { node: Text; offset: number }; end: { node: Text; offset: number } }> {
  const textNodes = collectTextNodes(root);
  const { text, locations } = buildVisibleTextMap(textNodes, root);
  return findKnownPlaceholders(text, knownKeys).map((match) => {
    const start = locations[match.start];
    const end = locations[match.end - 1];
    return {
      start: { node: textNodes[start.nodeIndex], offset: start.offset },
      end: { node: textNodes[end.nodeIndex], offset: end.offset + 1 },
    };
  });
}

function removeEmptyInlineTrail(node: Text, root: HTMLElement): void {
  let current: Node | null = node;
  while (current && current !== root) {
    const parent: Node | null = current.parentNode;
    if (!parent) return;

    const isEmptyText = current.nodeType === Node.TEXT_NODE && (current as Text).data === '';
    const isEmptyInline =
      current.nodeType === Node.ELEMENT_NODE &&
      isInlineTextFlowElement(current as Element) &&
      current.childNodes.length === 0;
    if (!isEmptyText && !isEmptyInline) return;

    parent.removeChild(current);
    current = parent;
  }
}

/**
 * Repairs visually contiguous, HTML-fragmented known tokens in memory. Stored HTML is never written back by this helper.
 * The first fragment's formatting wins; unknown tokens and fragments across visible boundaries remain untouched.
 */
export function mergeFragmentedPlaceholders(content: string, knownKeys: readonly string[]): string {
  if (!content || knownKeys.length === 0 || !content.includes('{')) return content;

  const container = document.createElement('div');
  container.innerHTML = content;
  const textNodes = collectTextNodes(container);
  if (textNodes.length === 0) return content;

  const { text, locations } = buildVisibleTextMap(textNodes, container);
  const matches = findKnownPlaceholders(text, knownKeys);
  let changed = false;

  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const match = matches[index];
    const start = locations[match.start];
    const end = locations[match.end - 1];
    if (start.nodeIndex !== end.nodeIndex) {
      const { nodeIndex: endNodeIndex } = end;
      changed = true;
      const firstNode = textNodes[start.nodeIndex];
      const lastNode = textNodes[endNodeIndex];
      firstNode.data = `${firstNode.data.slice(0, start.offset)}{{${match.key}}}`;

      for (let nodeIndex = start.nodeIndex + 1; nodeIndex < endNodeIndex; nodeIndex += 1) {
        textNodes[nodeIndex].data = '';
      }
      lastNode.data = lastNode.data.slice(end.offset + 1);

      for (let nodeIndex = endNodeIndex; nodeIndex > start.nodeIndex; nodeIndex -= 1) {
        const { [nodeIndex]: emptyCandidate } = textNodes;
        removeEmptyInlineTrail(emptyCandidate, container);
      }
    }
  }

  return changed ? container.innerHTML : content;
}

/** Replaces only known tokens after the in-memory compatibility repair. Unknown or misspelled tokens stay visible. */
export function replaceKnownPlaceholders(
  content: string,
  values: Readonly<Record<string, string>>,
  knownKeys: readonly string[],
): string {
  const normalized = mergeFragmentedPlaceholders(content, knownKeys);
  const knownKeySet = new Set(knownKeys);

  return normalized.replace(/\{\{(\w+)\}\}/g, (match, key: string) =>
    knownKeySet.has(key) && values[key] !== undefined ? values[key] : match,
  );
}

export function extractPlaceholderKeys(content: string): string[] {
  const matches = content.match(/\{\{(\w+)\}\}/g);
  if (!matches) return [];
  return Array.from(new Set(matches.map((match) => match.slice(2, -2))));
}
